namespace $.$$ {

	function sign_of( token: string ) {
		return [ ... token.matchAll( $mol_view_tree2_prop_signature ) ][ 0 ]?.groups
			?? { name: token, key: '', next: '' }
	}

	const plain = [ 'null', 'bool', 'number', 'string', 'locale', 'list', 'dict' ] as readonly string[]

	export class $bog_vmap_app_inspect extends $.$bog_vmap_app_inspect {

		override classes() {
			const own = this.Node().tree()
			return [ own, ... this.peers().filter( tree => tree.type !== own.type ) ]
		}

		class_title( next?: string ) {
			return this.Node().name( next )
		}

		@ $mol_mem_key
		title_draft( name: string, next?: string ) {
			return next ?? name
		}

		title_value( next?: string ) {
			return this.title_draft( this.class_title(), next )
		}

		@ $mol_action
		title_submit( event?: Event ) {

			const draft = this.title_value()
			if( !draft || draft === this.class_title() ) return

			this.class_title( draft )
		}


		override tools() {
			return [
				this.base_title(),
				this.total(),
				... this.title_note() ? [ this.Note() ] : [],
			] as readonly $mol_view_content[]
		}

		class_ready() {
			try {
				return Boolean( this.Node().tree() )
			} catch( error: unknown ) {
				if( $mol_promise_like( error ) ) return $mol_fail_hidden( error )
				return false
			}
		}

		base_title() {
			return this.Node().base()
		}

		override layout_shown( next?: boolean ) {
			return this.$.$mol_state_session.value( 'vmap_inspect_layout', next ) ?? super.layout_shown()
		}

		base_pick(): $mol_view_content {
			return this.bases().length ? this.Base() : this.base_title()
		}

		@ $mol_action
		base_submit( next?: Event | null ) {

			const typed = this.Base().filter_pattern().trim()
			if( !typed ) return null

			this.base( typed )

			this.Base().filter_pattern( '' )
			this.Base().showed( false )

			return null
		}

		@ $mol_mem
		ports() {
			return this.Lib().props_map( this.class_title() )
		}

		@ $mol_mem
		owners() {
			return this.Lib().props_owner( this.base_title() )
		}

		@ $mol_mem
		port_list() {

			const base = this.owners()
			const names = [ ... this.ports().keys() ]

			return [
				... names.filter( name => !base.has( name ) ),
				... names.filter( name => base.has( name ) ),
			]
		}

		@ $mol_mem
		own_ports() {
			return this.port_list().filter( name => !this.row_inherited( name ) )
		}

		@ $mol_mem
		inherited_ports() {
			return this.port_list().filter( name => this.row_inherited( name ) )
		}

		rows() {
			return this.own_ports().map( name => this.Row( name ) )
		}

		inherited_rows() {
			return this.inherited_ports().map( name => this.Row( name ) )
		}

		total() {
			const all = this.port_list().length
			const own = this.Node().prop_names().length
			return `${ all } портов, своих ${ own }`
		}

		port_node( name: string ) {
			return this.ports().get( name ) ?? this.Node().prop_tree( name ) ?? null
		}

		row_sign( name: string ) {
			return this.port_node( name )?.type ?? name
		}

		row_owner( name: string ) {
			return this.owners().get( name ) ?? ''
		}

		row_note( name: string ) {
			if( sign_of( this.row_sign( name ) ).name !== 'sub' ) return ''
			return this.sub_note()
		}

		row_inherited( name: string ) {
			return !this.Node().prop_names().includes( name )
		}

		binds() {
			return [ ... this.port_list(), ... this.shares() ]
		}

		share_row_name( name: string ) {
			return name
		}

		share_rows() {
			return this.shares().map( name => this.Share_row( name ) )
		}

		override body() {

			if( !this.class_ready() ) return [ this.Empty() ] as readonly $mol_view[]

			return super.body().filter(
				view => view !== this.Shares() || this.shares().length > 0
			) as readonly $mol_view[]
		}

		@ $mol_mem
		nodes() {

			const node = this.Node()
			const lib = this.Lib()

			const res = {} as Record< string, { klass: string, ports: readonly string[] } >

			for( const name of node.prop_names() ) {

				const val = node.prop_tree( name )?.kids[ 0 ] ?? null
				if( this.$.$bog_vmap_app_inspect_value_kind_of( val ) !== 'object' ) continue

				const klass = val!.type

				res[ name ] = {
					klass,
					ports: [ ... lib.props_map( klass ).keys() ],
				}

			}

			return res
		}

		override cell( sign: string, next?: $mol_tree2 | null ): $mol_tree2 | null {
			if( next === undefined ) return null
			return this.$.$mol_fail( new Error( `Узел без корня: ячейку для ${ sign } завести негде` ) )
		}

		nested() {
			return !this.Node().tree().type.startsWith( '$' )
		}

		row_cell( name: string ) {
			return this.nested() && Boolean( sign_of( this.row_sign( name ) ).next )
		}

		row_held( name: string ) {
			return this.row_cell( name ) ? this.cell( this.row_sign( name ) ) : null
		}

		override row_frozen( name: string ) {

			if( !this.editable() || !this.row_cell( name ) || this.row_inherited( name ) ) return ''

			const value = this.port_node( name )?.kids[ 0 ] ?? null

			return plain.includes( this.$.$bog_vmap_app_inspect_value_kind_of( value ) ) ? this.frozen_note() : ''
		}

		row_value( name: string, next?: $mol_tree2 ) {

			const decl = this.port_node( name )!

			if( next === undefined ) {

				const held = this.row_held( name )
				if( held ) return held

				const val = decl.kids[ 0 ] ?? decl

				if( this.row_inherited( name ) && val.type === '*' ) {
					return val.clone([ val.struct( '^' ) ])
				}

				return val

			}

			if( this.row_cell( name ) && plain.includes( this.$.$bog_vmap_app_inspect_value_kind_of( next ) ) ) {
				this.cell( this.row_sign( name ), next )
				return next
			}

			const node = this.Node()

			if( this.row_inherited( name ) ) node.prop_add( decl.type )

			const own = node.prop_tree( name )!
			node.prop_tree( name, own.clone([ next ]) )

			return next
		}

		row_keyed( name: string, next?: boolean ) {

			if( next === undefined ) return Boolean( sign_of( this.row_sign( name ) ).key )

			return this.Node().property( name ).key( next )
		}

		row_changeable( name: string, next?: boolean ) {

			const node = this.Node()

			if( this.nested() && !this.row_inherited( name ) ) {

				const live = this.port_node( name )?.kids[ 0 ]?.type === '<=>'
				if( next === undefined || next === live ) return live

				const value = this.row_value( name )
				const meta = sign_of( this.row_sign( name ) )
				const bare = meta.name + ( meta.key ? '*' : '' )

				if( next && plain.includes( this.$.$bog_vmap_app_inspect_value_kind_of( value ) ) ) {
					this.cell( bare + '?', value )
					return next
				}

				if( !next && this.row_held( name ) ) {
					this.cell( this.row_sign( name ), null )
					node.prop_add( bare )
					node.prop_tree( name, value.struct( bare, [ value ] ) )
					return next
				}

			}

			if( next === undefined ) return Boolean( sign_of( this.row_sign( name ) ).next )

			const value = this.port_node( name )?.kids[ 0 ] ?? null

			const kind = this.$.$bog_vmap_app_inspect_value_kind_of( value )

			if( kind === 'bind' || kind === 'get' ) {

				const prop = node.property( name )
				const own = prop.tree()
				const port = sign_of( own.type )
				const ref = value!.kids[ 0 ] ?? null
				const cell = sign_of( ref?.type ?? '' )
				const tail = next ? '?' : ''

				prop.tree( own.struct( port.name + port.key + tail, [
					value!.struct(
						next ? '<=>' : '<=',
						ref ? [ ref.struct( cell.name + cell.key + tail, ref.kids ) ] : [],
					),
				] ) )

				return next
			}

			if( kind === 'wire' ) {

				const origin = value!.kids[ 0 ]

				node.wire_add({
					name,
					node: origin?.type ?? '',
					prop: sign_of( origin?.kids[ 0 ]?.type ?? '' ).name,
					bidi: next,
				})

				return next
			}

			return node.property( name ).next( next )
		}

		row_drop( name: string ) {
			if( this.resettable( this.row_sign( name ) ) ) return this.reset( this.row_sign( name ), null )

			if( this.row_held( name ) ) this.cell( this.row_sign( name ), null )
			else this.Node().prop_drop( name )
		}

		override row_drop_hint( name: string ) {
			return this.resettable( this.row_sign( name ) )
				? 'Вернуть как в детали: значение вернётся к тому, что предлагает деталь'
				: 'Убрать свойство из документа'
		}

		style_dict() {

			const dict = this.Node().prop_decl( 'style' )?.kids[ 0 ] ?? null

			return dict?.type === '*' ? dict : null
		}

		flex_value( key: string, next?: string ) {

			const node = this.Node()
			const dict = this.style_dict()

			if( next === undefined ) {
				return this.$.$bog_vmap_lang_dict_get( dict, key )?.value ?? ''
			}

			const tree = node.tree()
			const base = dict ?? tree.struct( '*', [ tree.struct( '^' ) ] )

			const written = this.$.$bog_vmap_lang_dict_set(
				base,
				key,
				next ? tree.data( next ) : null,
			)

			if( ! node.prop_names().includes( 'style' ) ) node.prop_add( 'style' )

			node.prop_tree( 'style', node.prop_tree( 'style' )!.clone([ written ]) )

			return next
		}

	}

	const demo_source = [
		'$bog_vmap_app_inspect_demo_doc $mol_view',
		'	Price $mol_view title <= calc_result',
		'	Hero $mol_view sub / <= Price',
		'	Calc $mol_number',
		'	Card $bog_vmap_app_inspect_demo_doc_card',
		'	Ghost $bog_vmap_app_inspect_demo_doc_ghost',
		'	calc_result = Card price',
		'	label \\Всего',
		'	count 24',
		'	dense false',
		'	style * padding \\4px',
		'	sub / <= Hero',
		'$bog_vmap_app_inspect_demo_doc_card $mol_view',
		'	caption \\Карточка',
		'	price 0',
		'	sub / <= caption',
		'',
	].join( '\n' )

	export class $bog_vmap_app_inspect_demo extends $.$bog_vmap_app_inspect_demo {

		@ $mol_mem
		override source( next?: string ) {
			return next ?? demo_source
		}

		override names() {
			return this.Doc().names()
		}

		override trees() {
			return this.Doc().trees()
		}

		@ $mol_mem
		override klass( next?: string ) {
			return next ?? this.names()[ 0 ] ?? ''
		}

		override class_source( next?: string ) {
			return this.Doc().class_source( this.klass(), next )
		}

	}

}
